import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import useTokenStore from '../../stores/tokens';
import { authApi } from '../../utils/Intercepter';
import useModalStore from '../../stores/modal';
import { PURPLE, MINT, hexToRgba, mix } from '../../utils/designUtils';

const MailIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>
    </svg>
);
const LockIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
    </svg>
);
const EyeIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>
    </svg>
);

const IOS_URL = "https://apps.apple.com/kr/app/workitalk/id6767071009";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.workitalkapp&hl=ko";

function StoreBadge({ platform }) {
    const [hover, setHover] = React.useState(false);
    const isIOS = platform === "ios";

    const glyph = isIOS ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="2.5" width="12" height="19" rx="2.5"/>
            <path d="M12 7v6.5M9.5 11l2.5 2.5L14.5 11"/>
        </svg>
    ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 3.5c0-.6.65-.98 1.17-.67l13 8.5c.49.32.49 1.02 0 1.34l-13 8.5C5.65 21.48 5 21.1 5 20.5V3.5z"/>
        </svg>
    );

    return (
        <a
            href={isIOS ? IOS_URL : ANDROID_URL}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 10,
                height: 50,
                padding: "0 14px",
                borderRadius: 12,
                background: "#1f1d2e",
                color: "white",
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,.08)",
                boxShadow: hover
                    ? "0 12px 24px -10px rgba(31,29,46,.55)"
                    : "0 6px 16px -10px rgba(31,29,46,.45)",
                transform: hover ? "translateY(-1px)" : "none",
                transition: "transform 140ms, box-shadow 140ms",
            }}
        >
            <span style={{ display: "flex", color: "white", flexShrink: 0 }}>{glyph}</span>
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
                <span style={{ fontSize: 9.5, color: "rgba(255,255,255,.65)", letterSpacing: ".04em" }}>
                    {isIOS ? "iPhone 앱" : "Android 앱"}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-.01em", whiteSpace: "nowrap" }}>
                    {isIOS ? "App Store" : "Google Play"}
                </span>
            </span>
        </a>
    );
}

export default function Signin() {
    const { setTokens, clearTokens, setUser, setAuthenticated } = useTokenStore();
    const { openOrgSelectModal } = useModalStore();

    const [userEmail, setUserEmail] = useState('');
    const [userPassword, setUserPassword] = useState('');
    const [rememberUserId, setRememberUserId] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [focused, setFocused] = useState(null);

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    useEffect(() => {
        if (localStorage.getItem('rememberUserId')) {
            setRememberUserId(true);
            setUserEmail(localStorage.getItem('savedUserEmail') || '');
        }
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const isEmailValid = validateEmail(userEmail);
        const isAdminAccount = userEmail.trim() === 'admin';

        if (!isAdminAccount && !isEmailValid) {
            setEmailError(true);
            return;
        }
        setEmailError(false);

        const credentials = { userEmail, userPassword, clientId: "solegate_workitalk" };
        setLoading(true);

        try {
            const response = await authApi.login(credentials);
            const data = response.data;

            if (data.isApproved === 'N') {
                alert(`${data.organizationName} 기업의 승인 대기중입니다.\n관리자에게 문의하세요`);
                clearTokens();
                return;
            }

            onTokenSetting(data);

            if (data.isAdmin) {
                await processRemember(data);
                setAuthenticated(true);
            } else {
                const { orgUserList } = data;
                if (isMultiOrgUser(orgUserList)) {
                    if (orgUserList.some(org => org.default)) {
                        const defaultOrg = orgUserList.find(org => org.default);
                        handleOrgSelect(defaultOrg.organizationCode);
                    } else {
                        openOrgSelectModal();
                    }
                    await processRemember(data);
                } else {
                    await finishLogin(data);
                    await processRemember(data);
                    setAuthenticated(true);
                }
            }
        } catch (error) {
            if (error.response) {
                const status = error.response.status;
                if (status === 401 || status === 403) {
                    alert('이메일 또는 비밀번호가 올바르지 않습니다.');
                } else if (status === 400) {
                    alert('잘못된 요청입니다.');
                } else {
                    alert('로그인에 실패 하였습니다.');
                }
            } else {
                alert('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
            }
            clearTokens();
        } finally {
            setLoading(false);
        }
    };

    const onTokenSetting = async (data) => {
        localStorage.removeItem('currentOrganizationCode');
        localStorage.removeItem('currentOrganizationName');
        if (data.accessToken && data.refreshToken) {
            setTokens(data.accessToken, data.refreshToken);
        }
        setUser({
            userName: data.userName,
            userEmail: data.userEmail,
            orgUserList: data.orgUserList,
            isAdmin: data.admin,
        });
    };

    const isMultiOrgUser = (data) => Array.isArray(data) && data.length > 1;

    const finishLogin = (data) => {
        if (!data.orgUserList || data.orgUserList.length === 0) {
            setUser({ currentOrganizationCode: null, currentOrganizationName: null });
        } else {
            const org = data.orgUserList[0];
            localStorage.setItem('currentOrganizationCode', org.organizationCode);
            localStorage.setItem('currentOrganizationName', org.organizationName);
            setUser({
                currentOrganizationCode: org.organizationCode,
                currentOrganizationName: org.organizationName,
                currentRole: org.userRole,
                currentOrganizationApproved: org.isApproved ?? org.approved
            });
        }
    };

    function processRemember(data) {
        if (rememberUserId) {
            localStorage.setItem('rememberUserId', true);
            localStorage.setItem('savedUserEmail', data.userEmail);
        } else {
            localStorage.setItem('rememberUserId', false);
            localStorage.removeItem('savedUserEmail');
        }
        setUserPassword('');
    }

    const handleOrgSelect = (orgCode) => {
        const { user } = useTokenStore.getState();
        const selectedOrg = user?.orgUserList?.find(org => org.organizationCode === orgCode);
        if (selectedOrg) {
            localStorage.setItem('currentOrganizationCode', selectedOrg.organizationCode);
            localStorage.setItem('currentOrganizationName', selectedOrg.organizationName);
            setUser({
                currentOrganizationCode: selectedOrg.organizationCode,
                currentOrganizationName: selectedOrg.organizationName,
                currentRole: selectedOrg.userRole,
                currentOrganizationApproved: selectedOrg.isApproved ?? selectedOrg.approved
            });
        } else {
            alert('선택된 조직 정보를 찾을 수 없습니다.');
        }
        setAuthenticated(true);
    };

    const fieldStyle = (key) => ({
        display: "flex", alignItems: "center", gap: 10,
        background: "white",
        border: `1.5px solid ${focused === key ? PURPLE : "#ebeaf1"}`,
        borderRadius: 12,
        padding: "0 14px",
        height: 52,
        transition: "border-color 160ms, box-shadow 160ms",
        boxShadow: focused === key ? `0 0 0 4px ${hexToRgba(PURPLE, 0.10)}` : "none",
    });

    return (
        <div style={{
            background: `linear-gradient(135deg, ${mix(PURPLE, 0.88)} 0%, #fafaff 45%, ${mix(MINT, 0.88)} 100%)`,
            height: "calc(100vh - 61px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
        }}>
            {/* 배경 장식 블롭 */}
            <div aria-hidden style={{
                position: "absolute", top: -120, left: -120, width: 360, height: 360,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${hexToRgba(PURPLE, 0.35)}, transparent 70%)`,
                filter: "blur(20px)", pointerEvents: "none",
            }}/>
            <div aria-hidden style={{
                position: "absolute", bottom: -140, right: -100, width: 420, height: 420,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${hexToRgba(MINT, 0.30)}, transparent 70%)`,
                filter: "blur(20px)", pointerEvents: "none",
            }}/>

            <div style={{
                position: "relative",
                width: "100%",
                maxWidth: 420,
                background: "white",
                borderRadius: 24,
                padding: "44px 40px 32px",
                boxSizing: "border-box",
                boxShadow: "0 30px 80px -20px rgba(60,50,120,0.18), 0 1px 0 rgba(255,255,255,.6) inset",
                border: "1px solid rgba(255,255,255,.8)",
            }}>
                {/* 로고 */}
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <img src="/workitalk_icon.png" alt="WorkiTalk" width={44} height={44} style={{ display: "block" }}/>
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
                            <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: "-0.01em", color: "#1f1d2e" }}>
                                WorkiTalk
                            </span>
                            <span style={{ fontSize: 10, color: "#8b8a99", marginTop: 3, letterSpacing: ".08em" }}>
                                ENTERPRISE
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: "center", marginBottom: 28 }}>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1f1d2e", letterSpacing: "-0.01em" }}>
                        다시 오신 것을 환영합니다
                    </h1>
                    <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "#8b8a99" }}>
                        계정 정보를 입력하여 로그인해 주세요.
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: "flex", flexDirection: "column", gap: 14,
                        maxWidth: "none", margin: 0, padding: 0,
                        border: "none", borderRadius: 0, background: "none",
                    }}
                >
                    {/* 이메일 */}
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 500, color: "#5d5b6e", marginBottom: 6, display: "block" }}>
                            이메일
                        </label>
                        <div style={fieldStyle("email")}>
                            <MailIcon width={18} height={18} style={{ color: focused === "email" ? PURPLE : "#aaa9b7", flexShrink: 0 }}/>
                            <input
                                type="text"
                                value={userEmail}
                                onChange={(e) => {
                                    setUserEmail(e.target.value);
                                    if (e.target.value === 'admin' || validateEmail(e.target.value)) {
                                        setEmailError(false);
                                    }
                                }}
                                onFocus={() => setFocused("email")}
                                onBlur={(e) => {
                                    setFocused(null);
                                    const value = e.target.value;
                                    if (value && value !== 'admin' && !validateEmail(value)) {
                                        setEmailError(true);
                                    }
                                }}
                                placeholder="이메일"
                                style={{
                                    flex: 1, border: "none", outline: "none", boxShadow: "none",
                                    fontSize: 14, color: "#1f1d2e", background: "transparent",
                                    fontFamily: "inherit",
                                }}
                            />
                        </div>
                        {emailError && (
                            <p style={{ margin: "5px 0 0 4px", fontSize: 12, color: "#e05252" }}>
                                올바른 이메일 형식으로 입력해 주세요. (예: user@example.com)
                            </p>
                        )}
                    </div>

                    {/* 비밀번호 */}
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 500, color: "#5d5b6e", marginBottom: 6, display: "block" }}>
                            비밀번호
                        </label>
                        <div style={fieldStyle("pw")}>
                            <LockIcon width={18} height={18} style={{ color: focused === "pw" ? PURPLE : "#aaa9b7", flexShrink: 0 }}/>
                            <input
                                type={showPw ? "text" : "password"}
                                value={userPassword}
                                onChange={(e) => setUserPassword(e.target.value)}
                                onFocus={() => setFocused("pw")}
                                onBlur={() => setFocused(null)}
                                autoComplete="off"
                                placeholder="비밀번호"
                                style={{
                                    flex: 1, minWidth: 0, border: "none", outline: "none", boxShadow: "none",
                                    fontSize: 14, color: "#1f1d2e", background: "transparent",
                                    fontFamily: "inherit",
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw(!showPw)}
                                style={{
                                    background: "none", border: "none", padding: 4, cursor: "pointer",
                                    color: showPw ? PURPLE : "#aaa9b7", display: "flex", flexShrink: 0,
                                }}
                            >
                                <EyeIcon width={18} height={18}/>
                            </button>
                        </div>
                    </div>

                    {/* 이메일 저장 + 비밀번호 찾기 */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12.5, color: "#5d5b6e" }}>
                            <span
                                onClick={() => setRememberUserId(!rememberUserId)}
                                style={{
                                    width: 16, height: 16, borderRadius: 5,
                                    background: rememberUserId ? PURPLE : "white",
                                    border: `1.5px solid ${rememberUserId ? PURPLE : "#d0cfdb"}`,
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    transition: "all 140ms", cursor: "pointer", flexShrink: 0,
                                }}
                            >
                                {rememberUserId && (
                                    <svg width="10" height="10" viewBox="0 0 12 12">
                                        <path d="M2 6.5L5 9.5L10 3.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </span>
                            이메일 저장
                        </label>
                        <Link to="/reset-password" style={{ fontSize: 12.5, color: "#7a7989", textDecoration: "none" }}>
                            비밀번호 찾기
                        </Link>
                    </div>

                    {/* 로그인 버튼 */}
                    <button
                        type="submit"
                        disabled={loading || !userEmail || !userPassword}
                        style={{
                            marginTop: 8, height: 52, border: "none", borderRadius: 12, width: "100%", padding: 0,
                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                            color: "white", fontSize: 15, fontWeight: 600,
                            cursor: (loading || !userEmail || !userPassword) ? "not-allowed" : "pointer",
                            letterSpacing: ".01em",
                            boxShadow: `0 10px 24px -8px ${hexToRgba(PURPLE, 0.5)}`,
                            transition: "transform 120ms, box-shadow 120ms",
                            fontFamily: "inherit",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            opacity: (!userEmail || !userPassword) ? 0.7 : 1,
                        }}
                        onMouseDown={(e) => { if (!loading && userEmail && userPassword) e.currentTarget.style.transform = "scale(0.98)"; }}
                        onMouseUp={(e) => { e.currentTarget.style.transform = ""; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
                    >
                        {loading ? (
                            <svg width="20" height="20" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,.3)" strokeWidth="2.5" fill="none"/>
                                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2.5" fill="none"
                                        strokeDasharray="14 56" strokeLinecap="round">
                                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/>
                                </circle>
                            </svg>
                        ) : "로그인"}
                    </button>

                    <div style={{
                        display: "flex", alignItems: "center", gap: 12,
                        margin: "16px 0 6px", color: "#b6b5c1", fontSize: 11,
                    }}>
                        <div style={{ flex: 1, height: 1, background: "#ececf2" }}/>
                        <span>또는</span>
                        <div style={{ flex: 1, height: 1, background: "#ececf2" }}/>
                    </div>

                    <div style={{ textAlign: "center", fontSize: 13, color: "#7a7989" }}>
                        아직 계정이 없으신가요?{" "}
                        <Link to="/register" style={{ color: PURPLE, fontWeight: 600, textDecoration: "none" }}>
                            회원가입
                        </Link>
                    </div>

                    <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #f0eff5" }}>
                        <p style={{
                            margin: "0 0 10px", textAlign: "center",
                            fontSize: 12, fontWeight: 600, color: "#7a7989", letterSpacing: ".01em",
                        }}>
                            WorkiTalk 앱 다운로드
                        </p>
                        <div style={{ display: "flex", gap: 10 }}>
                            <StoreBadge platform="ios"/>
                            <StoreBadge platform="android"/>
                        </div>
                    </div>

                    <div style={{ textAlign: "center", fontSize: 11.5, color: "#b6b5c1", display: "flex", justifyContent: "center", alignItems: "center", gap: 10 }}>
                        <Link to="/privacy-policy" style={{ color: "#b6b5c1", textDecoration: "underline", textUnderlineOffset: 3 }}>
                            개인정보처리방침
                        </Link>
                        <span style={{ color: "#dddce6" }}>|</span>
                        <Link to="/inquiry" style={{ color: "#b6b5c1", textDecoration: "underline", textUnderlineOffset: 3 }}>
                            문의하기
                        </Link>
                    </div>
                </form>
            </div>
            
            <div style={{
                position: "absolute", bottom: 24, left: 0, right: 0,
                display: "flex", justifyContent: "center",
                fontSize: 11.5, color: "#9d9caa",
            }}>
                <span>© 2026 WorkiTalk Inc.</span>
            </div>
        </div>
    );
}
